import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Shield, Clock, Users, Send, Award, Zap } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { SERVICE_DATA } from "@/data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMAGES = {
  hero: "/images/hero-led.png",
  neon: "https://images.unsplash.com/photo-1674188473868-1881545979e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxuZW9uJTIwc2lnbiUyMG5pZ2h0JTIwZGFyayUyMGx1eHVyeXxlbnwwfHx8fDE3NzM2MDAzNjd8MA&ixlib=rb-4.1.0&q=85",
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: "easeOut" } }),
};

const stats = [
  { value: "500+", label: "Projects Completed" },
  { value: "150+", label: "Happy Clients" },
  { value: "8+", label: "Years Experience" },
  { value: "24/7", label: "Support Available" },
];

const whyChooseUs = [
  { icon: Shield, title: "Up to 10-Year Warranty", desc: "Comprehensive warranty extendable up to 10 years" },
  { icon: Award, title: "Red Seal Certified", desc: "Licensed electricians for safe, code-compliant work" },
  { icon: Clock, title: "Fast Turnaround", desc: "Quick delivery without compromising quality" },
  { icon: Users, title: "Expert Team", desc: "Skilled designers and installers at your service" },
];

const heroServices = ["Programmable LED Display", "Light Box", "Channel Letters", "Neon Sign", "Non-Illuminated Sign", "Non-Illuminated 3D Letters", "Pylon Sign", "Window/Car/Wall Graphics", "Banners", "Sign Repair", "Electrical Services", "Sign Installation", "City Sign Permit"];

function HeroQuoteForm() {
  const [form, setForm] = useState({ first_name: "", email: "", phone: "", service_interest: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Request sent! We'll get back to you within 24 hours.");
      setForm({ first_name: "", email: "", phone: "", service_interest: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.7, delay: 0.3 }}
      className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 lg:p-8 w-full"
      data-testid="hero-quote-form"
    >
      <div className="flex items-center gap-2 mb-5">
        <div className="w-2 h-2 rounded-full bg-gold"></div>
        <h3 className="font-heading text-lg font-bold text-white">Get a Free Quote</h3>
      </div>
      <div className="space-y-3">
        <input
          type="text"
          data-testid="hero-form-name"
          value={form.first_name}
          onChange={(e) => setForm({ ...form, first_name: e.target.value })}
          className="w-full bg-white/5 border border-white/10 focus:border-gold/50 text-white rounded-xl px-4 py-3 text-sm placeholder:text-white/25 focus:ring-1 focus:ring-gold/30 transition-all outline-none"
          placeholder="Your Name *"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            type="email"
            data-testid="hero-form-email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full bg-white/5 border border-white/10 focus:border-gold/50 text-white rounded-xl px-4 py-3 text-sm placeholder:text-white/25 focus:ring-1 focus:ring-gold/30 transition-all outline-none"
            placeholder="Email *"
          />
          <input
            type="tel"
            data-testid="hero-form-phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full bg-white/5 border border-white/10 focus:border-gold/50 text-white rounded-xl px-4 py-3 text-sm placeholder:text-white/25 focus:ring-1 focus:ring-gold/30 transition-all outline-none"
            placeholder="Phone"
          />
        </div>
        <select
          data-testid="hero-form-service"
          value={form.service_interest}
          onChange={(e) => setForm({ ...form, service_interest: e.target.value })}
          className="w-full bg-white/5 border border-white/10 focus:border-gold/50 text-white rounded-xl px-4 py-3 text-sm focus:ring-1 focus:ring-gold/30 transition-all outline-none"
        >
          <option value="" className="bg-[#131a23]">Select Service...</option>
          {heroServices.map((s) => (
            <option key={s} value={s} className="bg-[#131a23]">{s}</option>
          ))}
        </select>
        <textarea
          data-testid="hero-form-message"
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          rows={3}
          className="w-full bg-white/5 border border-white/10 focus:border-gold/50 text-white rounded-xl px-4 py-3 text-sm placeholder:text-white/25 focus:ring-1 focus:ring-gold/30 transition-all outline-none resize-none"
          placeholder="Tell us about your project... *"
        />
        {/* Not for people. Hidden, unlabelled, skipped by the tab key and
            excluded from autofill - a bot fills every input it finds in the
            HTML, and filling this one is the giveaway. The server bins
            anything that arrives with it set. */}
        <input
          type="text"
          name="website"
          value={form.website}
          onChange={(e) => setForm({ ...form, website: e.target.value })}
          tabIndex="-1"
          autoComplete="off"
          aria-hidden="true"
          data-testid="spam-trap"
          style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
        />

        <button
          type="submit"
          disabled={loading}
          data-testid="hero-form-submit"
          className="w-full bg-gold text-black font-bold py-3.5 rounded-xl hover:shadow-[0_0_25px_rgba(255,191,0,0.4)] transition-all duration-300 hover:-translate-y-0.5 flex items-center justify-center gap-2 text-sm disabled:opacity-50"
        >
          {loading ? "Sending..." : <><Send className="w-4 h-4" /> Request Free Quote</>}
        </button>
      </div>
      <p className="text-white/25 text-xs mt-3 text-center">We respond within 24 hours</p>
    </motion.form>
  );
}

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="VanSign Workshop" className="absolute inset-0 w-full h-full object-cover" style={{objectPosition: "center top"}} />
          <div className="hero-overlay absolute inset-0"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-16 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-center">
            {/* Left - Headline */}
            <motion.div initial="hidden" animate="visible" className="lg:col-span-3">
              <motion.p variants={fadeUp} custom={0} className="text-gold text-sm tracking-[0.3em] uppercase font-semibold mb-6">
                Vancouver's Premium Signage Company
              </motion.p>
              <motion.h1 variants={fadeUp} custom={1} className="font-heading text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold tracking-tighter uppercase text-white leading-[0.95] mb-8">
                Elevate Your<br />
                <span className="gold-shimmer">Business</span> With<br />
                Striking Signage
              </motion.h1>
              <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                <Link
                  to="/booking"
                  data-testid="hero-cta-booking"
                  className="bg-gold text-black font-bold px-6 sm:px-8 py-3 sm:py-4 rounded-full hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all duration-300 hover:-translate-y-1 flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  Book Consultation <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="tel:+17783220801"
                  data-testid="hero-cta-phone"
                  className="bg-transparent border border-white/20 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full hover:bg-white/5 hover:border-gold/50 transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <Phone className="w-4 h-4" /> Call Now
                </a>
              </motion.div>
            </motion.div>

            {/* Right - Quote Form */}
            <div className="lg:col-span-2">
              <HeroQuoteForm />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section data-testid="stats-section" className="section-alt border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-10 sm:py-16 md:py-20 grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-10">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="stat-card text-center py-4"
            >
              <p className="font-heading text-3xl md:text-4xl font-bold text-gold mb-1">{stat.value}</p>
              <p className="text-sm text-white/50">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section data-testid="services-preview" className="py-16 sm:py-28 md:py-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mb-10 sm:mb-16">
            <p className="text-gold text-sm tracking-[0.3em] uppercase font-semibold mb-4">What We Do</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">Our Core Services</h2>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 lg:gap-6">
            {SERVICE_DATA.slice(0, 4).map((service, i) => (
              <motion.div
                key={service.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
              >
                <Link
                  to={`/services/${service.slug}`}
                  className="group relative block rounded-xl sm:rounded-2xl overflow-hidden aspect-square"
                  data-testid={`service-card-${i}`}
                >
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-5">
                    <h3 className="font-heading text-xs sm:text-sm lg:text-base font-bold text-white group-hover:text-gold transition-colors">{service.title}</h3>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 sm:mt-14 text-center">
            <Link
              to="/services"
              data-testid="view-more-services-btn"
              className="inline-flex items-center gap-2 text-gold hover:text-white transition-colors text-sm font-bold uppercase tracking-widest"
            >
              View All Services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-choose-section" className="py-16 sm:py-28 md:py-40 section-alt">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-gold text-sm tracking-[0.3em] uppercase font-semibold mb-4">Why VANSIGN</p>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-6 sm:mb-8">
                Built for Businesses<br />That Want to <span className="gold-shimmer">Stand Out</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {whyChooseUs.map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-gold" />
                    </div>
                    <div>
                      <h4 className="font-heading font-bold text-white text-sm mb-1">{item.title}</h4>
                      <p className="text-white/50 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="mt-10">
                <Link
                  to="/about"
                  data-testid="learn-more-about-btn"
                  className="inline-flex items-center gap-2 bg-transparent border border-white/20 text-white px-8 py-4 rounded-full hover:bg-white/5 hover:border-gold/50 transition-all"
                >
                  Learn More About Us <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden img-zoom">
                <img src={IMAGES.neon} alt="Neon signage" className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process - From Concept to Installation */}
      <section data-testid="process-section" className="py-16 sm:py-28 md:py-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left - Header + Image (sticky on desktop) */}
            <div>
              <div className="lg:sticky lg:top-32">
                <div className="flex items-center gap-3 mb-4 lg:mb-6">
                  <div className="w-8 h-px bg-gold"></div>
                  <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">The Process</p>
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-6xl font-bold lg:font-light tracking-tighter text-white leading-[1.1] mb-6 lg:mb-8">
                  From Concept to <span className="gold-shimmer">Installation</span>
                </h2>
                <p className="hidden lg:block text-white/40 text-base mb-10 max-w-md leading-relaxed">
                  Every sign we build follows a transparent, client-first process. Here's exactly what to expect.
                </p>
                <div className="relative rounded-2xl overflow-hidden lg:h-[55vh] mb-10 lg:mb-0">
                  <motion.img
                    initial={{ scale: 1.05 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    src="/images/process-neon.png"
                    alt="Custom neon sign installation"
                    className="w-full lg:h-full object-contain lg:object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#0f1419] to-transparent"></div>
                </div>
              </div>
            </div>

            {/* Right - Timeline Steps */}
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-[19px] lg:left-[23px] top-4 bottom-4 w-[2px] bg-gradient-to-b from-gold/60 via-gold/20 to-transparent"></div>

              {[
                { num: "01", title: "Initial Consultation", desc: "You reach out and tell us what kind of sign you need. We arrange an in-person visit or free on-site survey.", descLg: "You reach out and tell us what kind of sign you need. We send you samples, arrange an in-person visit, or conduct a free on-site survey to assess your location and requirements." },
                { num: "02", title: "Estimate & Deposit", desc: "We provide a detailed estimate. Once approved, a 50% deposit secures your production slot.", descLg: "We provide a detailed estimate tailored to your project. Once approved, a 50% deposit is required to get started. This secures your production slot and kicks off the design phase." },
                { num: "03", title: "Custom Design", desc: "Our team creates a custom concept with up to two rounds of revisions to nail your vision.", descLg: "Our design team creates a custom concept aligned with your brand. We include up to two rounds of revisions to ensure the design is exactly what you envisioned." },
                { num: "04", title: "Design Approval", desc: "You review and approve. Production only begins after your sign-off. zero surprises.", descLg: "You review and approve the final design. Production only begins after your sign-off, ensuring we build exactly what you want with zero surprises." },
                { num: "05", title: "Fabrication", desc: "We build your sign. Vinyl prints take 2-5 days, channel letters about 2 weeks.", descLg: "Our team builds your sign. Simple signs like vinyl prints take 2-5 business days. Larger projects like channel letters take approximately 2 weeks." },
                { num: "06", title: "Review & Installation", desc: "Inspect your sign, then we schedule installation. Remaining balance due at delivery.", descLg: "Visit our shop to inspect your sign, or we send photos for approval. After sign-off, we schedule installation and the remaining balance is due at delivery." },
              ].map((step, i) => (
                <motion.div
                  key={step.num}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-10% 0px" }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="relative pl-14 lg:pl-16 pb-8 last:pb-0"
                  data-testid={`process-step-${step.num}`}
                >
                  {/* Timeline dot */}
                  <div className="absolute left-0 top-0 w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-[#0f1419] border-2 border-gold/40 flex items-center justify-center z-10">
                    <span className="font-mono text-xs lg:text-sm font-bold text-gold">{step.num}</span>
                  </div>

                  {/* Card */}
                  <div className="glass-card rounded-xl p-5 lg:p-7">
                    <h3 className="font-heading text-base lg:text-xl font-bold text-white mb-2 lg:mb-3">{step.title}</h3>
                    <p className="text-white/45 text-sm lg:text-base leading-relaxed lg:hidden">{step.desc}</p>
                    <p className="text-white/45 text-base leading-relaxed hidden lg:block">{step.descLg}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-16 sm:py-28 md:py-40 relative overflow-hidden">
        <div className="absolute inset-0 bg-gold/5"></div>
        <div className="absolute top-0 left-0 right-0 gold-line"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
              Ready to Make Your Business <span className="gold-shimmer">Shine?</span>
            </h2>
            <p className="text-white/50 text-lg mb-10 max-w-2xl mx-auto">
              Get a free consultation and discover how our custom signage solutions can transform your business visibility and attract more customers.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/booking"
                data-testid="cta-booking-btn"
                className="bg-gold text-black font-bold px-10 py-4 rounded-full hover:shadow-[0_0_20px_rgba(255,191,0,0.4)] transition-all duration-300 hover:-translate-y-1 flex items-center gap-2"
              >
                Book Free Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+17783220801"
                data-testid="cta-phone-btn"
                className="bg-transparent border border-white/20 text-white px-10 py-4 rounded-full hover:bg-white/5 hover:border-gold/50 transition-all flex items-center gap-2"
              >
                <Phone className="w-4 h-4" /> (778) 322-0801
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
